import React, { useEffect, useMemo, useRef, useState } from 'react'
import { hoyLocal } from '../utils/fecha'
import { useNavigate } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CCard, CCardBody, CCardHeader,
  CTabs, CTabList, CTab, CTabPanel, CSpinner,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { fmtMoneda } from '../utils/moneda'
import { numeroWhatsApp } from '../utils/whatsapp'
import CIcon from '@coreui/icons-react'
import { cilWarning } from '@coreui/icons'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import WwSearchField from '../components/ww/WwSearchField'
import { usePendiente } from '../components/ww/pendiente'

// [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
// mapa compartido entre el render de la grilla y el valor plano del export.
const MODALIDAD_TXT: Record<number, string> = { 1: 'DIARIO', 7: 'SEMANAL', 15: 'QUINCENAL', 30: 'MENSUAL', 60: 'LIBRE' }

const columnas: WwColumn[] = [
  { key: 'atrazoNro', title: 'Nro', type: 'number', link: true },
  { key: 'atrazoFecha', title: 'Fecha', type: 'date' },
  { key: 'atrazoHora', title: 'Hora' },
  { key: 'prestamoNro', title: 'Prestamo Nro', type: 'number' },
  {
    key: 'prestamoMPago', title: 'Modalidad',
    // [RGM] 2026-09-17 (pedido Rafael): TEXTO de la modalidad (labels GX
    // 1=DIARIO..60=LIBRE) — igual que la grilla de Préstamos.
    render: (row) => MODALIDAD_TXT[Number(row.prestamoMPago)] ?? String(row.prestamoMPago ?? '').trim(),
    exportValue: (row) => MODALIDAD_TXT[Number(row.prestamoMPago)] ?? String(row.prestamoMPago ?? '').trim(),
  },
  { key: 'clienteCedula', title: 'Cédula', type: 'id' },
  { key: 'clienteNombre', title: 'Nombre' },
  { key: 'clienteCelular', title: 'WhatsApp' },
  { key: 'prestamoProximoCobro', title: 'Proximo Cobro', type: 'date' },
  { key: 'zonaNombre', title: 'Ruta' },
  { key: 'cobrador', title: 'Cobrador' },
  { key: 'prestamoFecha', title: 'Prestamo Fecha', type: 'date' },
  { key: 'prestamoSaldo', title: 'Saldo', type: 'currency' },
  { key: 'prestamoCuota', title: 'Cuota', type: 'currency' },
  { key: 'prestamoDiasMora', title: 'Mora', type: 'number' },
  { key: 'prestamoAtrazos', title: 'Atrasos', type: 'number' },
  { key: 'prestamoAtrazosCondenados', title: 'Condenados', type: 'number' },
  { key: 'utilidadAtrasos', title: 'Utilidad Atrasos', type: 'currency' },
  { key: 'totalAtrasosCondenados', title: 'Total Atrasos Condenados', type: 'currency' },
  { key: 'atrazoObservacion', title: 'Observacion' },
  { key: 'atrazoCreadoPor', title: 'Creado Por' },
]

const hoy = () => hoyLocal()

const AtrasosPage: React.FC = () => {
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // ── Filtros rápidos: tipo de atraso ──
  const [filtroTipo, setFiltroTipo] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Préstamo elegido en el tab Atraso Ágil
  const [prestamoSel, setPrestamoSel] = useState<any>(null)
  // Fila en edición (atrazoww.cs:3081-3083 'DO UPDATE' → webobject atrazo Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [errorModal, setErrorModal] = useState('')
  // Form del ATRASO ÁGIL (diseño RGM): cuotas, observación y próximo cobro.
  // La fecha del atraso es la del día (como el flujo móvil).
  const [formAgil, setFormAgil] = useState({ cuotas: '1', observacion: '', proximoCobro: hoy() })
  const [guardandoAgil, setGuardandoAgil] = useState(false)
  const [exitoAgil, setExitoAgil] = useState('')
  // Form del modal de EDICIÓN (atrazoww UPD — Lote 1, se conserva tal cual)
  const [form, setForm] = useState({
    prestamoNro: '', empresaCodigo: empresa, cuotas: 1,
    fecha: hoy(),
    proximoCobro: hoy(), observacion: '',
  })

  // Filas filtradas por tipo (client-side, patrón Préstamos)
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroTipo && String(row.atrazoTipoAtrazo ?? '').trim() !== filtroTipo) return false
    return true
  }), [rows, filtroTipo])

  const tiposAtrazo = useMemo(() => [...new Set(rows.map((r) => String(r.atrazoTipoAtrazo ?? '').trim()).filter(Boolean))], [rows])
  const chips = [
    filtroTipo ? { clave: 'tipo', label: `Tipo: ${filtroTipo}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => setFiltroTipo('')
  const quitarFiltro = (clave: string) => { if (clave === 'tipo') setFiltroTipo('') }

  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/atrasos?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  // Modificar (atrazoww.cs:3081-3083): carga la fila en el form y abre el modal.
  // El modal queda SOLO para edición — el alta vive en el tab Atraso Ágil.
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      prestamoNro: String(fila.prestamoNro ?? ''),
      empresaCodigo: empresa,
      cuotas: Number(fila.atrazoCuotas ?? 1),
      // Fecha/hora del atraso NO son editables en el webobject GX
      // (atrazo.cs:1367-1394): se conservan.
      fecha: String(fila.atrazoFecha ?? '').slice(0, 10) || hoy(),
      proximoCobro: String(fila.atrazoProximoCobro ?? '').slice(0, 10),
      observacion: String(fila.atrazoObservacion ?? '') || '',
    })
    setAbierto(true)
  }

  // Registrar atraso ágil — POST /api/atrasos (OperacionesController.cs:16-19):
  // prestamoNro, empresaCodigo, cuotas, fecha (hoy), proximoCobro, observacion.
  const registrarAgil = async (e: React.FormEvent) => {
    e.preventDefault()
    setExitoAgil('')
    setError('')
    setGuardandoAgil(true)
    try {
      await api.post('/atrasos', {
        prestamoNro: Number(prestamoSel.prestamoNro), empresaCodigo: empresa,
        cuotas: Number(formAgil.cuotas), fecha: hoy(), proximoCobro: formAgil.proximoCobro,
        observacion: formAgil.observacion || undefined,
      })
      setExitoAgil('Atraso registrado exitosamente.')
      // Limpiar el form ágil; el préstamo se mantiene para registros rápidos
      // consecutivos del mismo cliente.
      setFormAgil({ cuotas: '1', observacion: '', proximoCobro: hoy() })
      cargar()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setGuardandoAgil(false)
    }
  }

  // Guardar edición (atrazoww.cs:3081-3083 — DO UPDATE: webobject atrazo Mode="UPD").
  const guardarEdicion = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    try {
      // Solo campos editables del form (atrazo.cs:1367-1394, 1446-1448):
      // Cuotas, Próx. cobro y Observación.
      await api.put(`/atrasos/${editando.atrazoNro}`, {
        empresaCodigo: form.empresaCodigo,
        cuotas: Number(form.cuotas),
        proximoCobro: form.proximoCobro,
        observacion: form.observacion || undefined,
      })
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    }
  }

  const eliminar = (fila: any) => {
    // atrazoww.cs:3089-3091 — DO DELETE: webobject atrazo Mode="DLT" con
    // AtrazoNro. El backend replica el BC DLT (atrazo_bc.cs:849-894) que
    // revierte los contadores del préstamo y borra el atraso (BC000415).
    if (!window.confirm(`¿Eliminar el atraso ${fila.atrazoNro}?`)) return
    api.delete(`/atrasos/${fila.atrazoNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Atraso eliminado.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const contactar = (fila: any) => {
    const celular = String(fila.clienteCelular ?? '').trim()
    if (!celular) {
      setAviso('Cliente sin celular')
      return
    }
    window.open(`https://wa.me/${numeroWhatsApp(celular)}?text=${encodeURIComponent('Hola, te escribo desde SiprinIA')}`, '_blank')
  }
  const rowActions: WwRowAction[] = [
    { label: 'Contactar', onClick: contactar },
  ]

  const [totalReg, setTotalReg] = useState(rows.length)

  // ─── Buscador de préstamos (mismo patrón de AbonosPage: GET /prestamos) ───
  // Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
  // empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99"). [RGM] 2026-10-02 (F)

  const fmtFecha = (v: any) => {
    const s = String(v ?? '')
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
  }

  const fmtFechaValida = (v: any) => {
    // DateTime.MinValue del préstamo (sin próximo cobro asignado) → ''.
    const s = String(v ?? '')
    if (!s || s.startsWith('0001')) return ''
    return fmtFecha(s)
  }

  const buscarPrestamos = async (texto: string): Promise<any[]> => {
    const t = texto.trim().toLowerCase()
    const todos = await api.get<any[]>(`/prestamos?empresa=${empresa}`)
    return todos
      .filter((p) =>
        String(p.prestamoNro ?? '').includes(texto.trim()) ||
        String(p.clienteCedula ?? '').includes(texto.trim()) ||
        String(p.clienteNombre ?? '').toLowerCase().includes(t),
      )
      .slice(0, 10)
  }

  const renderPrestamo = (p: any) => ({
    titulo: `${p.clienteNombre} — Préstamo #${p.prestamoNro}`,
    subtitulo: `Ruta: ${p.ruta ?? '—'}`,
    detalle: [`Saldo: ${fmtMoneda(p.prestamoSaldo)}`, `Cuota: ${fmtMoneda(p.prestamoCuota)}`],
  })

  const seleccionarPrestamo = (p: any) => setPrestamoSel(p)

  const limpiarPrestamo = () => setPrestamoSel(null)

  const resumenPrestamo = (p: any) => ({
    titulo: `${p.clienteNombre} — Préstamo #${p.prestamoNro}`,
    lineas: [
      `Saldo: ${fmtMoneda(p.prestamoSaldo)}`,
      `Cuota: ${fmtMoneda(p.prestamoCuota)}`,
      `Ruta: ${p.ruta ?? '—'}`,
    ],
  })

  // Hint del próximo cobro: si el préstamo tiene uno asignado se muestra la
  // fecha; si no, el texto del diseño (se calcula con la modalidad del préstamo,
  // el día de cobro y domingos/festivos — cálculo GX que ocurre en el BC).
  const fechaProximoCobro = prestamoSel ? fmtFechaValida(prestamoSel.prestamoProximoCobro) : ''
  const hintProximoCobro = fechaProximoCobro
    ? `Próximo cobro actual: ${fechaProximoCobro}`
    : 'Se calcula según modalidad, día de cobro, domingos y festivos'

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Atraso</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {exitoAgil && <CAlert color="success" dismissible onClose={() => setExitoAgil('')}>{exitoAgil}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <CTabs defaultActiveItemKey="agil">
        <CTabList variant="tabs">
          <CTab itemKey="agil">Atraso Ágil</CTab>
          <CTab itemKey="historial">Historial</CTab>
        </CTabList>

        {/* Tab Atraso Ágil — formulario de registro rediseñado (diseño RGM).
            Mismo patrón de búsqueda de AbonosPage + alta POST /api/atrasos. */}
        <CTabPanel itemKey="agil" className="pt-3">
          <CCard>
            <CCardHeader className="bg-body-tertiary fw-semibold">Registrar Atraso</CCardHeader>
            <CCardBody>
              <CForm onSubmit={registrarAgil}>
                <CRow className="g-3">
                  <CCol md={8}>
                    <CFormLabel>Cliente o préstamo</CFormLabel>
                    <WwSearchField
                      placeholder="Cédula, nombre o N.º préstamo"
                      onBuscar={buscarPrestamos}
                      renderItem={renderPrestamo}
                      onSeleccionar={seleccionarPrestamo}
                      limpiar
                      onLimpiar={limpiarPrestamo}
                      seleccionado={prestamoSel !== null}
                      resumen={prestamoSel ? resumenPrestamo(prestamoSel) : undefined}
                    />
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Saldo actual</CFormLabel>
                    <CFormInput value={prestamoSel ? fmtMoneda(prestamoSel.prestamoSaldo) : ''} readOnly />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Cuotas de atraso</CFormLabel>
                    {/* Atraso Ágil registra SIEMPRE 1 atraso (réplica opcionesatraso
                        del GX: acción 'Ingresar Atraso' → AtrasoDirecto sin cuotas,
                        "Se Acumula 1 Atraso para Condenar") — campo fijo, no
                        editable [RGM]. El POST manda Number(formAgil.cuotas) = 1. */}
                    <CFormInput type="number" min="1" value="1" readOnly />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Observación</CFormLabel>
                    <CFormInput value={formAgil.observacion} onChange={(e) => setFormAgil({ ...formAgil, observacion: capitalizarPalabras(e.target.value) })} />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Próximo cobro</CFormLabel>
                    <CFormInput type="date" value={formAgil.proximoCobro} onChange={(e) => setFormAgil({ ...formAgil, proximoCobro: e.target.value })} />
                    {prestamoSel && (
                      <small className="text-body-secondary">{hintProximoCobro}</small>
                    )}
                  </CCol>
                  {/* Botón solo visible con préstamo elegido (diseño RGM) */}
                  {prestamoSel && (
                    <CCol xs={12} className="d-flex justify-content-end">
                      <CButton color="primary" type="submit" disabled={!formAgil.cuotas || Number(formAgil.cuotas) < 1 || guardandoAgil}>
                        {guardandoAgil ? (
                          <><CSpinner size="sm" className="me-1" />Registrando…</>
                        ) : (
                          'Registrar Atraso'
                        )}
                      </CButton>
                    </CCol>
                  )}
                </CRow>
              </CForm>
            </CCardBody>
          </CCard>
        </CTabPanel>

        {/* Tab Historial — grilla WwGrid EXISTENTE (atrazoww). El ALTA vive en el
            tab Atraso Ágil, por eso el grid ya no muestra el botón Agregar. */}
        <CTabPanel itemKey="historial" className="pt-3">
          <WwGrid
            filters={
              <WwFilterBar
                selects={[
                  { key: 'tipo', label: 'Todos los Tipos', opciones: tiposAtrazo, valor: filtroTipo, onChange: setFiltroTipo },
                ]}
                chips={chips}
                onQuitar={quitarFiltro}
                onLimpiar={limpiarFiltros}
              />
            }
            title="Atraso"
            columns={columnas}
            rows={visibleRows}
            onTotalChange={setTotalReg}
            loading={cargando}
            onEditar={abrirEditar}
            busquedaPlaceholder="Buscar por atraso, préstamo o cliente..." 
            onEliminar={eliminar}
            rowActions={rowActions}
            onRowLink={(colKey, row) => {
              if (colKey === 'atrazoNro') navigate(`/atrasos/${row.atrazoNro}`)
            }}
          />
        </CTabPanel>
      </CTabs>

      {/* Modal de EDICIÓN (atrazoww UPD — Lote 1, se conserva sin cambios de
          comportamiento: solo se abre con una fila para Modificar). */}
      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>Modificar atraso</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardarEdicion}>
            {/* Tarjeta única (patrón formularios nuevos) — Empresa del contexto */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilWarning} size="sm" /> Datos del Atraso
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}><CFormLabel>Préstamo Nro</CFormLabel><CFormInput type="number" value={form.prestamoNro} disabled onChange={(e) => setForm({ ...form, prestamoNro: e.target.value })} /></CCol>
                  <CCol md={6}><CFormLabel>Cuotas en atraso</CFormLabel><CFormInput type="number" value={form.cuotas} onChange={(e) => setForm({ ...form, cuotas: Number(e.target.value) })} /></CCol>
                  <CCol md={6}><CFormLabel>Fecha</CFormLabel><CFormInput type="date" value={form.fecha} disabled onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></CCol>
                  <CCol md={6}><CFormLabel>Próx. cobro</CFormLabel><CFormInput type="date" value={form.proximoCobro} onChange={(e) => setForm({ ...form, proximoCobro: e.target.value })} /></CCol>
                  <CCol md={12}><CFormLabel>Observación</CFormLabel><CFormInput value={form.observacion} onChange={(e) => setForm({ ...form, observacion: capitalizarPalabras(e.target.value) })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" onClick={() => formRef.current?.requestSubmit()}>Guardar cambios</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AtrasosPage